import { useState, useEffect, useEffectEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Save, Trash2, ArchiveRestore } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { investorNavItems } from '@/config/entity-navigation';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  useInvestor,
  useCreateInvestor,
  useUpdateInvestor,
  useValidateIban,
  useValidateSwift,
  useDeleteInvestor,
  useUnarchiveInvestor,
} from '@/hooks/react-query/useInvestors';
import {
  buildMutationErrorHandler,
  extractValidationErrors,
  scrollToFirstError,
} from '@/utils/form-errors';
import type {
  InvestorCreateData,
  DraftInvestorContact,
  InvestorType,
  KycStatus,
  AmlStatus,
} from '@/types/investor';
import { InvestorProfileForm } from './InvestorProfileForm';
import { InvestorBankingForm } from './InvestorBankingForm';
import type { IbanStatus, SwiftStatus } from './InvestorBankingForm';
import type { BankCodeOption } from '@/hooks/react-query/useBankCodes';
import type { IbanValidation } from '@/types/banking';
import { InvestorContactsTable } from './InvestorContactsTable';
import { PendingContactsSection } from './PendingContactsSection';
import { InvestorDocumentsForm } from './InvestorDocumentsForm';
import { InvestorEmailsTab } from './InvestorEmailsTab';
import { InvestorInvestmentOverview } from '@/pages/investor/InvestorOverview';
import { InvestorChangeRequests } from '@/pages/investor/InvestorChangeRequests';

type FormErrors = Record<string, string>;

type InvestorFormData = {
  name: string;
  investor_type: InvestorType;
  legal_form: string;
  country: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  postcode: string;
  tax_id: string;
  date_of_incorporation: string;
  kyc_status: KycStatus | '';
  aml_status: AmlStatus | '';
  bank_account_name: string;
  iban: string;
  account_number: string;
  swift_bic: string;
  bank_name: string;
  bank_address: string;
  bank_country: string;
};

function toInvestorCreateData(formData: InvestorFormData): InvestorCreateData {
  // The IBAN is the account identifier when present; otherwise the plain
  // account number is, and the two are stored in distinct columns.
  const hasIban = !!formData.iban.trim();
  return {
    name: formData.name,
    investor_type: formData.investor_type,
    legal_form: formData.legal_form || undefined,
    country: formData.country,
    // Send address fields as-is (including empty strings) rather than
    // collapsing empties to undefined. With exclude_unset on the backend,
    // an omitted field is never written, so clearing an address field would
    // otherwise silently keep the old value. Empty strings are coerced to
    // null server-side, letting the user clear these fields.
    address_line_1: formData.address_line_1,
    address_line_2: formData.address_line_2,
    city: formData.city,
    postcode: formData.postcode,
    taxpayer_reference_number: formData.tax_id || undefined,
    incorporation_date: formData.date_of_incorporation || undefined,
    kyc_status: formData.kyc_status || undefined,
    aml_status: formData.aml_status || undefined,
    bank_account_name: formData.bank_account_name,
    iban: hasIban ? formData.iban : '',
    bank_account_number: hasIban ? '' : formData.account_number,
    bank_name: formData.bank_name,
    bank_swift_code: formData.swift_bic,
    bank_address: formData.bank_address,
    bank_country: formData.bank_country,
    is_individual: formData.investor_type === 'individual',
    is_direct_investor: true,
    is_high_risk_country: false,
    is_high_risk_sector: false,
    has_complex_legal_structure: false,
  };
}

export default function InvestorFormPage() {
  const navigate = useNavigate();
  const { investorId } = useParams();
  const isEdit = !!investorId;

  const [searchParams, setSearchParams] = useSearchParams();
  const [errors, setErrors] = useState<FormErrors>({});
  const activeTab =
    searchParams.get('tab') ?? (isEdit ? 'overview' : 'profile');

  const handleTabChange = (newTab: string) => {
    setSearchParams({ tab: newTab });
  };

  // React Query hooks
  const {
    data: investor,
    isLoading,
    error: fetchError,
  } = useInvestor(investorId);
  const createInvestorMutation = useCreateInvestor();
  const updateInvestorMutation = useUpdateInvestor();
  const validateIbanMutation = useValidateIban();
  const validateSwiftMutation = useValidateSwift();
  const deleteInvestorMutation = useDeleteInvestor();
  const unarchiveInvestorMutation = useUnarchiveInvestor();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Form data
  const [formData, setFormData] = useState<InvestorFormData>({
    name: '',
    investor_type: '' as InvestorType,
    legal_form: '',
    country: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    postcode: '',
    tax_id: '',
    date_of_incorporation: '',
    kyc_status: '',
    aml_status: '',
    bank_account_name: '',
    iban: '',
    account_number: '',
    swift_bic: '',
    bank_name: '',
    bank_address: '',
    bank_country: '',
  });

  const [ibanStatus, setIbanStatus] = useState<IbanStatus>('idle');
  const [swiftStatus, setSwiftStatus] = useState<SwiftStatus>('idle');

  // Bank name derived from the IBAN (empty when the IBAN maps to no known bank).
  // A non-empty suggestion means a registry match, which locks the name field to
  // the authoritative registry value.
  const [bankNameSuggestion, setBankNameSuggestion] = useState('');
  const bankNameLocked = ibanStatus === 'valid' && !!bankNameSuggestion;

  const [pendingContacts, setPendingContacts] = useState<
    DraftInvestorContact[]
  >([]);

  // Keyboard shortcuts
  const formShortcuts = [
    {
      key: 'Escape',
      description: 'Cancel and go back',
      action: () => navigate('/app/investors'),
      category: 'Navigation',
    },
    {
      key: 'ctrl+s',
      description: 'Save investor',
      action: handleSave,
      category: 'Form Actions',
    },
  ];

  useKeyboardShortcuts('investor-form', formShortcuts);

  // Sync investor data to form when investor data loads (for edit mode)
  const [syncedInvestorId, setSyncedInvestorId] = useState<string | null>(null);
  if (isEdit && investor?.id && investor.id !== syncedInvestorId) {
    setSyncedInvestorId(investor.id);
    setFormData({
      name: investor.name,
      investor_type: investor.investor_type,
      legal_form: investor.legal_form || '',
      country: investor.country || '',
      address_line_1: investor.address_line_1 || '',
      address_line_2: investor.address_line_2 || '',
      city: investor.city || '',
      postcode: investor.postcode || '',
      tax_id: investor.taxpayer_reference_number || '',
      date_of_incorporation: investor.incorporation_date || '',
      kyc_status: investor.kyc_status,
      aml_status: investor.aml_status,
      bank_account_name: investor.bank_account_name || '',
      iban: investor.iban || '',
      account_number: investor.bank_account_number || '',
      swift_bic: investor.bank_swift_code || '',
      bank_name: investor.bank_name || '',
      bank_address: investor.bank_address || '',
      bank_country: investor.bank_country || '',
    });
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Investor name is required';
    }

    if (!formData.investor_type) {
      newErrors.investor_type = 'Investor type is required';
    }

    // A typed IBAN must be valid; clearing it switches to account number + SWIFT.
    if (formData.iban.trim() && ibanStatus === 'invalid') {
      newErrors.iban =
        'Enter a valid IBAN, or clear it to use an account number + SWIFT';
    }

    // Without an IBAN, the SWIFT/BIC is the bank anchor - reject a malformed one.
    if (
      !formData.iban.trim() &&
      formData.swift_bic.trim() &&
      swiftStatus === 'invalid'
    ) {
      newErrors.swift_bic = 'Enter a valid SWIFT/BIC code';
    }

    // A non-IBAN account doesn't carry its routing details in an identifier, so
    // require the account number, bank name and bank country once any non-IBAN
    // bank detail is entered.
    const hasNonIbanDetails =
      !!formData.account_number.trim() || !!formData.swift_bic.trim();
    if (!formData.iban.trim() && hasNonIbanDetails) {
      if (!formData.account_number.trim()) {
        newErrors.account_number =
          'Account number is required for a non-IBAN account';
      }
      if (!formData.bank_name.trim()) {
        newErrors.bank_name = 'Bank name is required for a non-IBAN account';
      }
      if (!formData.bank_country.trim()) {
        newErrors.bank_country =
          'Bank country is required for a non-IBAN account';
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
      return false;
    }

    return true;
  };

  async function handleSave() {
    if (!validateForm()) {
      return;
    }

    const investorData = toInvestorCreateData(formData);

    try {
      if (isEdit && investorId) {
        await updateInvestorMutation.mutateAsync({
          id: investorId,
          data: investorData,
        });
      } else {
        await createInvestorMutation.mutateAsync({
          ...investorData,
          contacts: pendingContacts.length
            ? pendingContacts.map(({ _uiKey, ...rest }) => rest)
            : undefined,
        });
      }
      navigate('/app/investors');
    } catch (error: unknown) {
      const pendingContactFieldsToGeneral = Object.fromEntries(
        Object.keys(extractValidationErrors(error))
          .filter((field) => field.startsWith('contacts.'))
          .map((field) => [field, 'general'])
      );
      buildMutationErrorHandler(setErrors, {
        fallbackMessage: 'Failed to save investor',
        fieldMap: pendingContactFieldsToGeneral,
      })(error);
    }
  }

  async function handleDelete() {
    if (!investorId) {
      return;
    }

    deleteInvestorMutation.mutate(investorId, {
      onSuccess: () => {
        navigate('/app/investors');
      },
      onSettled: () => {
        setShowDeleteDialog(false);
      },
    });
  }

  async function handleUnarchive() {
    if (!investorId) {
      return;
    }
    await unarchiveInvestorMutation.mutateAsync(investorId);
  }

  // mutateAsync is a stable reference in react-query, so it can be used
  // directly without a ref or memoization (the React Compiler handles the rest).
  const { mutateAsync: validateIbanRequest } = validateIbanMutation;
  const { mutateAsync: validateSwiftRequest } = validateSwiftMutation;

  // What the registry made of an IBAN the user entered: its SWIFT, matched
  // bank and country land in form state.
  const applyIbanValidation = (validation: IbanValidation) => {
    // A non-empty bank_name means the IBAN matched a registry bank.
    const matched = !!validation.bank_name;
    setBankNameSuggestion(matched ? validation.bank_name : '');
    setFormData((prev) => ({
      ...prev,
      swift_bic: validation.swift_code,
      // On a registry match the name is authoritative, so it replaces any
      // typed value; on a miss the user's free-text name is preserved.
      bank_name: matched ? validation.bank_name : prev.bank_name,
      bank_country: validation.country_code,
    }));
  };

  // The same for the saved IBAN of an investor being edited. The stored SWIFT
  // is left as it is: only the registry's name and country are refreshed, so
  // opening the form doesn't quietly rewrite what was saved.
  const applyHydratedIban = (validation: IbanValidation) => {
    const matched = !!validation.bank_name;
    setBankNameSuggestion(matched ? validation.bank_name : '');
    setFormData((prev) => ({
      ...prev,
      // Reflect the live registry name on a match so the locked field never
      // shows a stale variant saved before the bank was matched.
      bank_name: matched ? validation.bank_name : prev.bank_name,
      bank_country: validation.country_code,
    }));
  };

  // Validate a SWIFT/BIC for a non-IBAN account and infer its country (and bank
  // name when the registry knows it). Only meaningful when there's no IBAN.
  const validateSwift = async (swift: string) => {
    if (formData.iban.trim() || !swift.trim()) {
      setSwiftStatus('idle');
      return;
    }

    setSwiftStatus('validating');

    try {
      const result = (await validateSwiftRequest({ swift })) as {
        is_valid: boolean;
        bank_name: string;
        country_code: string;
      };
      setSwiftStatus(result.is_valid ? 'valid' : 'invalid');
      setFormData((prev) => {
        if (prev.swift_bic !== swift) {
          return prev;
        }
        return {
          ...prev,
          bank_country: result.is_valid
            ? result.country_code
            : prev.bank_country,
          // Offer the registry bank name only when the user hasn't typed one.
          bank_name:
            result.bank_name && !prev.bank_name
              ? result.bank_name
              : prev.bank_name,
        };
      });
    } catch {
      setSwiftStatus('invalid');
    }
  };

  // For a saved non-IBAN account, validate the stored SWIFT so the status and
  // country chip reflect it without the user re-typing.
  const validateLoadedSwift = useEffectEvent((swift: string) => {
    void validateSwiftRequest({ swift }).then(
      (result) => {
        const r = result as { is_valid: boolean; country_code: string };
        setSwiftStatus(r.is_valid ? 'valid' : 'invalid');
        if (r.is_valid) {
          setFormData((prev) => ({ ...prev, bank_country: r.country_code }));
        }
      },
      () => setSwiftStatus('invalid')
    );
  });

  // The IBAN field checks a saved IBAN itself, so only a non-IBAN account's
  // stored SWIFT needs classifying here.
  useEffect(() => {
    if (!isEdit || investor?.iban) return;
    if (investor?.bank_swift_code) {
      validateLoadedSwift(investor.bank_swift_code);
    }
  }, [isEdit, investor?.iban, investor?.bank_swift_code]);

  // Simplified handler for profile fields (no IBAN validation)
  const handleProfileInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Clear error callback for profile form
  const handleClearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handler for banking fields. The IBAN and SWIFT are validated on blur, not
  // per keystroke, so editing resets the field to an unvalidated (idle) state.
  const handleBankingInputChange = (field: string, value: string) => {
    setFormData((prev) => {
      // When the IBAN changes, drop the IBAN-derived SWIFT, country and any
      // registry-derived bank name (recognised by it matching the last
      // suggestion) so stale matched data can't be saved; a name the user typed
      // is preserved.
      if (field === 'iban') {
        const wasRegistryName =
          !!bankNameSuggestion && prev.bank_name === bankNameSuggestion;
        return {
          ...prev,
          iban: value,
          swift_bic: '',
          bank_name: wasRegistryName ? '' : prev.bank_name,
          bank_country: '',
        };
      }
      // Editing the SWIFT in non-IBAN mode invalidates the derived country.
      if (field === 'swift_bic') {
        return { ...prev, swift_bic: value, bank_country: '' };
      }
      return { ...prev, [field]: value };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (field === 'iban') {
      setIbanStatus('idle');
      setSwiftStatus('idle');
      setBankNameSuggestion('');
    }
    if (field === 'swift_bic') {
      setSwiftStatus('idle');
    }
  };

  // Picking a bank from the registry dropdown sets the name and the bank's
  // country together, so the two stay consistent with the registry entry. For a
  // non-IBAN account it also fills the SWIFT/BIC from the registry, so the
  // server can relink bank_code_id from the BIC (and the required BIC is set).
  // An IBAN-anchored account keeps its IBAN-derived SWIFT untouched.
  const handleBankSelect = (bank: BankCodeOption) => {
    const ibanAnchored = ibanStatus === 'valid';
    const fillSwift = !ibanAnchored && !!bank.bic;
    setFormData((prev) => ({
      ...prev,
      bank_name: bank.bank_name,
      bank_country: bank.country_code || prev.bank_country,
      swift_bic: fillSwift ? bank.bic : prev.swift_bic,
    }));
    if (fillSwift) {
      setSwiftStatus('valid');
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.bank_name;
      delete next.bank_country;
      return next;
    });
  };

  // Validate the SWIFT/BIC when the user leaves the field (non-IBAN accounts).
  const handleSwiftBlur = (value: string) => {
    void validateSwift(value);
  };

  const saving =
    createInvestorMutation.isPending || updateInvestorMutation.isPending;

  const investorName = investor?.name || formData.name;
  const isInAnyFund = (investor?.fund_relationships ?? []).some(
    (relationship) => relationship.status !== 'EXITED'
  );
  const activeNavItem = investorNavItems.find((i) => i.value === activeTab);
  const breadcrumbSegments =
    isEdit && investorName
      ? [
          { label: 'Investors', href: '/app/investors' },
          { label: investorName, href: `/app/investors/${investorId}` },
          ...(activeNavItem ? [{ label: activeNavItem.label }] : []),
        ]
      : [
          { label: 'Investors', href: '/app/investors' },
          { label: 'New Investor' },
        ];

  return (
    <DataLoader
      data={isEdit ? investor : {}}
      isLoading={isEdit && isLoading}
      error={fetchError}
    >
      {() => (
        <div className="mx-auto p-6 space-y-6">
          {/* Breadcrumb */}
          <PageBreadcrumb
            segments={breadcrumbSegments}
            actions={
              <div className="flex items-center gap-2">
                {isEdit && investor?.is_archived && (
                  <Badge variant="secondary">Archived</Badge>
                )}
                {isEdit &&
                  (investor?.is_archived ? (
                    <Button
                      variant="outline"
                      onClick={handleUnarchive}
                      disabled={unarchiveInvestorMutation.isPending}
                      className="flex items-center space-x-2"
                    >
                      <ArchiveRestore className="h-4 w-4" />
                      <span>
                        {unarchiveInvestorMutation.isPending
                          ? 'Unarchiving...'
                          : 'Unarchive'}
                      </span>
                    </Button>
                  ) : (
                    <span
                      title={
                        isInAnyFund
                          ? 'Remove the investor from its funds before deleting it'
                          : undefined
                      }
                    >
                      <Button
                        variant="destructive"
                        onClick={() => setShowDeleteDialog(true)}
                        disabled={
                          isInAnyFund || deleteInvestorMutation.isPending
                        }
                        className="flex items-center space-x-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </Button>
                    </span>
                  ))}
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{saving ? 'Saving...' : 'Save Investor'}</span>
                </Button>
              </div>
            }
          />

          {/* Error Display */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-red-700">{errors.general}</div>
            </div>
          )}

          {/* Form Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="space-y-6"
          >
            {/* Investment Overview Tab - Only in view mode */}
            {isEdit && (
              <TabsContent value="overview" className="space-y-6">
                <InvestorInvestmentOverview investorId={investorId!} />
              </TabsContent>
            )}

            {/* Profile & Entity Tab */}
            <TabsContent value="profile" className="space-y-6">
              <InvestorProfileForm
                formData={formData}
                errors={errors}
                onInputChange={handleProfileInputChange}
                onClearError={handleClearError}
              />
              {!isEdit && (
                <PendingContactsSection
                  contacts={pendingContacts}
                  onChange={setPendingContacts}
                />
              )}
            </TabsContent>

            {/* Banking Tab */}
            <TabsContent value="banking">
              <InvestorBankingForm
                formData={{
                  iban: formData.iban,
                  account_number: formData.account_number,
                  bank_name: formData.bank_name,
                  bank_account_name: formData.bank_account_name,
                  swift_bic: formData.swift_bic,
                  bank_address: formData.bank_address,
                  bank_country: formData.bank_country,
                }}
                ibanStatus={ibanStatus}
                swiftStatus={swiftStatus}
                bankNameLocked={bankNameLocked}
                accountNumberError={errors.account_number}
                bankNameError={errors.bank_name}
                bankCountryError={errors.bank_country}
                onInputChange={handleBankingInputChange}
                onBankSelect={handleBankSelect}
                onValidateIban={(iban) => validateIbanRequest({ iban })}
                onIbanValidated={applyIbanValidation}
                onIbanHydrated={applyHydratedIban}
                onIbanStatusChange={setIbanStatus}
                onSwiftBlur={handleSwiftBlur}
              />
            </TabsContent>

            {/* Contacts Tab */}
            {isEdit && (
              <TabsContent value="contacts">
                <InvestorContactsTable
                  investorId={investorId!}
                  contacts={investor?.contacts || []}
                />
              </TabsContent>
            )}

            {/* Documents Tab */}
            <TabsContent value="documents">
              <InvestorDocumentsForm
                investorId={investorId}
                investorName={formData.name}
              />
            </TabsContent>

            {/* Emails Tab - Only in view mode */}
            {isEdit && (
              <TabsContent value="emails">
                <InvestorEmailsTab investorId={investorId!} />
              </TabsContent>
            )}

            {/* Change Data Tab - Only in view mode */}
            {isEdit && (
              <TabsContent value="change-data">
                <InvestorChangeRequests investorId={investorId!} />
              </TabsContent>
            )}
          </Tabs>

          <ConfirmationDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            variant="destructive"
            title="Delete investor"
            description={`Delete ${investorName || 'this investor'}? If the investor is linked to any transactions it will be archived instead of deleted, so its financial history is preserved.`}
            confirmLabel="Delete"
            onConfirm={handleDelete}
            isLoading={deleteInvestorMutation.isPending}
          />
        </div>
      )}
    </DataLoader>
  );
}
